import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Scale, Loader2, AlertTriangle, Info,
  Check, X, Utensils, ThumbsUp, ThumbsDown, Target, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { TIER_NAMES } from '@/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LipidTargetProgramPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('daily-lipid');
  const [loading, setLoading] = useState(true);
  const [lipidData, setLipidData] = useState(null);

  useEffect(() => {
    fetchLipidAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLipidAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/myinsul/lipid-target/analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLipidData(response.data);
    } catch (error) {
      toast.error('Failed to load lipid analysis');
    } finally {
      setLoading(false);
    }
  };

  const analysis = lipidData?.analysis;
  const limits = lipidData?.limits;

  const getStatusColor = (status) => {
    if (status === 'within_limit' || status === 'above_minimum') return 'text-emerald-600';
    if (status === 'borderline') return 'text-amber-600';
    return 'text-red-600';
  };

  const getStatusBgColor = (status) => {
    if (status === 'within_limit' || status === 'above_minimum') return 'bg-emerald-50 border-emerald-200';
    if (status === 'borderline') return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const tabs = [
    { id: 'daily-lipid', label: 'Daily Lipid Consumption', icon: Target },
    { id: 'culprit-foods', label: 'My Culprit Foods', icon: ThumbsDown },
    { id: 'good-foods', label: 'My Good Foods', icon: ThumbsUp }
  ];

  // Pie chart colors
  const COLORS = {
    saturatedFat: ['#ef4444', '#fecaca'],
    transFat: ['#f97316', '#fed7aa'],
    solubleFiber: ['#22c55e', '#bbf7d0'],
    insolubleFiber: ['#3b82f6', '#bfdbfe']
  };

  // Prepare pie chart data
  const getSaturatedFatData = () => {
    if (!analysis?.daily_averages) return [];
    const value = analysis.daily_averages.saturated_fat_g || 0;
    const limit = limits?.saturated_fat_g || 20;
    return [
      { name: 'Consumed', value: Math.min(value, limit) },
      { name: 'Remaining', value: Math.max(0, limit - value) }
    ];
  };

  const getTransFatData = () => {
    if (!analysis?.daily_averages) return [];
    const value = analysis.daily_averages.trans_fat_g || 0;
    // For trans fat, any amount is bad, so show it as consumed vs ideal (0)
    return [
      { name: 'Consumed', value: value },
      { name: 'Ideal (0g)', value: Math.max(0.5, 2 - value) } // Show small reference
    ];
  };

  const getSolubleFiberData = () => {
    if (!analysis?.daily_averages) return [];
    const value = analysis.daily_averages.soluble_fiber_g || 0;
    const min = limits?.soluble_fiber_g_min || 5;
    const max = limits?.soluble_fiber_g_max || 10;
    return [
      { name: 'Consumed', value: value },
      { name: 'Target Min', value: Math.max(0, min - value) }
    ];
  };

  const getInsolubleFiberData = () => {
    if (!analysis?.daily_averages) return [];
    const value = analysis.daily_averages.insoluble_fiber_g || 0;
    const min = limits?.insoluble_fiber_g_min || 15;
    return [
      { name: 'Consumed', value: value },
      { name: 'Target Min', value: Math.max(0, min - value) }
    ];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/myinsul')}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                    Lipid Target Program
                  </h1>
                  <p className="text-xs text-muted-foreground">Fat, Fiber & Cholesterol Management</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchLipidAnalysis}
                disabled={loading}
                className="rounded-full"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-medium">
                {TIER_NAMES.cdm}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              data-testid={`${tab.id}-tab`}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id 
                ? "rounded-full px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500" 
                : "rounded-full px-6 py-2"
              }
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : !lipidData?.has_data ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-8 text-center"
          >
            <AlertTriangle className="w-16 h-16 mx-auto text-amber-400 mb-4" />
            <h2 className="text-xl font-bold mb-2">No Data Available</h2>
            <p className="text-muted-foreground">{lipidData?.message || 'Start logging your meals to see lipid analysis!'}</p>
          </motion.div>
        ) : (
          <AnimatePresence mode="wait">
            {/* Daily Lipid Consumption Tab */}
            {activeTab === 'daily-lipid' && (
              <motion.div
                key="daily-lipid"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Date Range */}
                <div className="text-sm text-muted-foreground text-center">
                  Analysis period: {lipidData?.date_range?.start} to {lipidData?.date_range?.end} ({analysis?.days_analyzed || 0} days analyzed)
                </div>

                {/* Pie Charts Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Saturated Fat */}
                  <div className={`bg-white rounded-2xl shadow-lg p-6 border ${getStatusBgColor(analysis?.status?.saturated_fat)}`}>
                    <h3 className="font-bold text-center mb-2">Saturated Fat</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getSaturatedFatData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {getSaturatedFatData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.saturatedFat[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(analysis?.status?.saturated_fat)}`}>
                        {analysis?.daily_averages?.saturated_fat_g?.toFixed(1) || 0}g
                      </div>
                      <div className="text-xs text-muted-foreground">Daily avg / {limits?.saturated_fat_g}g limit</div>
                    </div>
                  </div>

                  {/* Trans Fat */}
                  <div className={`bg-white rounded-2xl shadow-lg p-6 border ${getStatusBgColor(analysis?.status?.trans_fat)}`}>
                    <h3 className="font-bold text-center mb-2">Trans Fat</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getTransFatData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {getTransFatData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.transFat[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(analysis?.status?.trans_fat)}`}>
                        {analysis?.daily_averages?.trans_fat_g?.toFixed(1) || 0}g
                      </div>
                      <div className="text-xs text-muted-foreground">Daily avg / 0g ideal</div>
                    </div>
                  </div>

                  {/* Soluble Fiber */}
                  <div className={`bg-white rounded-2xl shadow-lg p-6 border ${getStatusBgColor(analysis?.status?.soluble_fiber)}`}>
                    <h3 className="font-bold text-center mb-2">Soluble Fiber</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getSolubleFiberData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {getSolubleFiberData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.solubleFiber[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(analysis?.status?.soluble_fiber)}`}>
                        {analysis?.daily_averages?.soluble_fiber_g?.toFixed(1) || 0}g
                      </div>
                      <div className="text-xs text-muted-foreground">Daily avg / {limits?.soluble_fiber_g_min}-{limits?.soluble_fiber_g_max}g target</div>
                    </div>
                  </div>

                  {/* Insoluble Fiber */}
                  <div className={`bg-white rounded-2xl shadow-lg p-6 border ${getStatusBgColor(analysis?.status?.insoluble_fiber)}`}>
                    <h3 className="font-bold text-center mb-2">Insoluble Fiber</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getInsolubleFiberData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                          >
                            {getInsolubleFiberData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS.insolubleFiber[index]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${getStatusColor(analysis?.status?.insoluble_fiber)}`}>
                        {analysis?.daily_averages?.insoluble_fiber_g?.toFixed(1) || 0}g
                      </div>
                      <div className="text-xs text-muted-foreground">Daily avg / {limits?.insoluble_fiber_g_min}-{limits?.insoluble_fiber_g_max}g target</div>
                    </div>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-amber-800 mb-2">Recommended Daily Limits (Based on 2000 calorie diet)</h3>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li><strong>Saturated Fat:</strong> Maximum 20g/day (should be kept as low as possible)</li>
                        <li><strong>Trans Fat:</strong> 0g/day is ideal - should be completely avoided</li>
                        <li><strong>Total Fiber:</strong> 25-38g/day recommended</li>
                        <li><strong>Soluble Fiber:</strong> 5-10g/day minimum</li>
                        <li><strong>Insoluble Fiber:</strong> 15-25g/day minimum</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Educational Content */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-gray-800 mb-3">Understanding Lipids and Fiber</h3>
                      <div className="text-sm text-gray-600 space-y-3">
                        <p>
                          <strong>Saturated Fats</strong> are found in animal products like red meat, butter, cheese, and full-fat dairy. 
                          They raise LDL ("bad") cholesterol levels in your blood, which increases the risk of heart disease and stroke. 
                          Limiting saturated fat intake helps maintain healthy cholesterol levels.
                        </p>
                        <p>
                          <strong>Trans Fats</strong> are artificially created through hydrogenation and found in some processed foods, 
                          fried foods, and baked goods. They are the most harmful type of fat - they raise LDL cholesterol while 
                          simultaneously lowering HDL ("good") cholesterol. Even small amounts significantly increase cardiovascular risk.
                        </p>
                        <p>
                          <strong>Soluble Fiber</strong> dissolves in water and forms a gel-like substance in the gut. It actively 
                          helps lower LDL cholesterol by binding to cholesterol particles and removing them from the body. 
                          Found in oats, beans, apples, citrus fruits, and psyllium.
                        </p>
                        <p>
                          <strong>Insoluble Fiber</strong> doesn't dissolve in water and adds bulk to stool, promoting regular 
                          bowel movements. While it doesn't directly lower cholesterol, it helps with weight management and 
                          reduces the risk of type 2 diabetes. Found in whole grains, nuts, vegetables, and wheat bran.
                        </p>
                        <p className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <strong>The LDL Connection:</strong> To optimize your LDL cholesterol levels, focus on reducing saturated 
                          and trans fats while increasing soluble fiber intake. This combination can reduce LDL cholesterol by 10-15%.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Overall Assessment */}
                {analysis?.overall_assessment && (
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
                    <h3 className="font-bold mb-2">Overall Assessment</h3>
                    <p>{analysis.overall_assessment}</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Culprit Foods Tab */}
            {activeTab === 'culprit-foods' && (
              <motion.div
                key="culprit-foods"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <ThumbsDown className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">My Culprit Foods</h2>
                      <p className="text-sm text-muted-foreground">
                        Foods high in saturated/trans fats and low in fiber from the past week
                      </p>
                    </div>
                  </div>

                  {analysis?.culprit_foods?.length > 0 ? (
                    <div className="space-y-4">
                      {analysis.culprit_foods.map((food, index) => (
                        <div 
                          key={`lipid-culprit-${food.name}-${index}`}
                          className="flex items-start gap-4 p-4 bg-red-50 rounded-xl border border-red-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {food.rank || index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{food.name}</h3>
                              <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded-full">
                                {food.meal_type}
                              </span>
                              <span className="text-xs text-muted-foreground">{food.date}</span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-sm mb-2">
                              <span className="text-red-600">
                                <strong>Sat Fat:</strong> {food.saturated_fat_g}g
                              </span>
                              <span className="text-orange-600">
                                <strong>Trans Fat:</strong> {food.trans_fat_g}g
                              </span>
                              <span className="text-green-600">
                                <strong>Sol Fiber:</strong> {food.soluble_fiber_g}g
                              </span>
                              <span className="text-blue-600">
                                <strong>Insol Fiber:</strong> {food.insoluble_fiber_g}g
                              </span>
                            </div>
                            <p className="text-sm text-red-700">{food.reason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ThumbsUp className="w-12 h-12 mx-auto mb-2 text-emerald-300" />
                      <p>Great job! No culprit foods identified this week.</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Good Foods Tab */}
            {activeTab === 'good-foods' && (
              <motion.div
                key="good-foods"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <ThumbsUp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">My Good Foods</h2>
                      <p className="text-sm text-muted-foreground">
                        Foods low in saturated/trans fats and high in fiber from the past week
                      </p>
                    </div>
                  </div>

                  {analysis?.good_foods?.length > 0 ? (
                    <div className="space-y-4">
                      {analysis.good_foods.map((food, index) => (
                        <div 
                          key={`lipid-good-${food.name}-${index}`}
                          className="flex items-start gap-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200"
                        >
                          <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                            {food.rank || index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{food.name}</h3>
                              <span className="text-xs bg-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full">
                                {food.meal_type}
                              </span>
                              <span className="text-xs text-muted-foreground">{food.date}</span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-sm mb-2">
                              <span className="text-red-600">
                                <strong>Sat Fat:</strong> {food.saturated_fat_g}g
                              </span>
                              <span className="text-orange-600">
                                <strong>Trans Fat:</strong> {food.trans_fat_g}g
                              </span>
                              <span className="text-green-600">
                                <strong>Sol Fiber:</strong> {food.soluble_fiber_g}g
                              </span>
                              <span className="text-blue-600">
                                <strong>Insol Fiber:</strong> {food.insoluble_fiber_g}g
                              </span>
                            </div>
                            <p className="text-sm text-emerald-700">{food.reason}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-amber-300" />
                      <p>No standout healthy foods identified. Try adding more fiber-rich, low-fat options!</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </main>
    </div>
  );
};

export default LipidTargetProgramPage;
